import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const empty = { first_name: "", last_name: "", email: "", mobile: "", password: "" };

export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/users");
    setRows(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/users/${editing}`, form);
      else await api.post("/admin/users", form);
      toast.success("Saved");
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this admin user?")) return;
    await api.delete(`/admin/users/${id}`); load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Admin Users</h1>
          <p className="text-zinc-500 mt-2 text-sm">Total: {rows.length}</p>
        </div>
        <button data-testid="add-admin-user-btn" onClick={() => { setForm(empty); setEditing(null); setOpen(true); }}
          className="btn-primary"><Plus size={16} /> Add User</button>
      </div>

      <div className="card-clean overflow-x-auto">
        <table className="w-full table-clean">
          <thead className="bg-zinc-50">
            <tr>
              {["ID", "First Name", "Last Name", "Email", "Mobile", "Actions"].map((h) => (
                <th key={h} className="text-left px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[var(--line)] hover:bg-zinc-50">
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{r.id.slice(0, 8)}</td>
                <td className="px-4 py-3">{r.first_name}</td>
                <td className="px-4 py-3">{r.last_name}</td>
                <td className="px-4 py-3">{r.email}</td>
                <td className="px-4 py-3">{r.mobile}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => { setEditing(r.id); setForm({ ...r, password: "" }); setOpen(true); }}
                      className="p-1.5 rounded hover:bg-zinc-100"><Pencil size={14} /></button>
                    <button onClick={() => del(r.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="card-clean p-8 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl font-bold mb-6">{editing ? "Edit" : "Add"} Admin User</h3>
            <div className="space-y-3">
              {[["first_name", "First Name"], ["last_name", "Last Name"], ["email", "Email"], ["mobile", "Mobile"]].map(([k, l]) => (
                <div key={k}>
                  <label className="text-xs uppercase tracking-widest text-zinc-500">{l}</label>
                  <input data-testid={`admin-user-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="mt-1 w-full border border-[var(--line)] rounded-lg px-3 py-2.5" />
                </div>
              ))}
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500">Password {editing && "(leave blank to keep)"}</label>
                <input data-testid="admin-user-password" type="text" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="mt-1 w-full border border-[var(--line)] rounded-lg px-3 py-2.5 font-mono" />
              </div>
            </div>
            <div className="flex gap-3 mt-6 justify-end">
              <button onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>
              <button data-testid="admin-user-save" onClick={save} className="btn-primary">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
